import { zeusApi } from 'src/store/api';
import { type ResponseWithPaginationMeta } from 'src/shared/api/types';
import {
  type Company,
  type CompanyPayload,
  type SelfFundedAccount,
  type SelfFundedAccountFullData,
  type SelfFundedAccountTopUp
} from 'src/shared/types/company';
import type { BaseQueryError } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import FileDownload from 'js-file-download';

type GetCompaniesArg = {
  searchValue: string;
};

type PatchSelfFundingAlertLimitArg = {
  companyId: string;
  lowOnFundsThreshold: number;
};

type DownloadRepaymentSummaryWireFileArg = {
  companyId: string;
  amount: number;
};

const extendedApi = zeusApi.injectEndpoints({
  endpoints: build => ({
    editCompanies: build.mutation<Company, Array<Pick<Company, 'id'> & CompanyPayload>>({
      query: data => ({
        url: `/holding/companies`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: ['Companies']
    }),
    getCompanies: build.query<ResponseWithPaginationMeta<Company>, void>({
      query: () => `/holding/companies`,
      providesTags: ['Companies']
    }),
    getSelfFundedAccounts: build.query<SelfFundedAccount[], void>({
      query: () => `/self-funding/accounts`,
      providesTags: ['SelfFunding']
    }),
    getSelfFundedAccount: build.query<SelfFundedAccountFullData, string>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const companyAccountRes = await fetchWithBQ({
          url: `/self-funding/account/${arg}`
        });
        const companyAccountTopUpsRes = await fetchWithBQ({
          url: `/self-funding/account/${arg}/top-ups`
        });
        const error = companyAccountRes.error || companyAccountTopUpsRes.error;
        if (error) {
          return { error };
        }
        return {
          data: {
            account: companyAccountRes.data as SelfFundedAccount,
            topUps: (companyAccountTopUpsRes.data as any).topUps as SelfFundedAccountTopUp[]
          }
        };
      },
      providesTags: ['SelfFunding']
    }),

    getCompaniesPaginateAll: build.query<Company[], GetCompaniesArg>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        let currentPage = 0;
        let lastPage = 1;
        const res: Company[] = [];

        do {
          currentPage += 1;
          const companiesResult = await fetchWithBQ({
            url: `/holding/companies`,
            params: {
              page: currentPage,
              search: arg.searchValue,
              size: 50
            }
          });
          if (companiesResult.error) return { error: companiesResult.error };
          const companiesResultData = companiesResult.data as ResponseWithPaginationMeta<Company>;
          res.push(...companiesResultData.data);

          lastPage = companiesResultData.meta.lastPage;
        } while (currentPage < lastPage);

        return { data: res };
      },
      providesTags: ['Companies']
    }),
    updateSelfFundingLowOnFundsThreshold: build.mutation<void, PatchSelfFundingAlertLimitArg>({
      query: data => ({
        url: `/self-funding/account/${data.companyId}/limit`,
        body: {
          lowOnFundsThreshold: data.lowOnFundsThreshold
        },
        method: 'PATCH'
      }),
      invalidatesTags: ['SelfFunding', 'Limits']
    }),
    downloadRepaymentSummaryWireFile: build.mutation({
      queryFn: async (data: DownloadRepaymentSummaryWireFileArg, api, extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            method: 'POST',
            body: {
              amount: data.amount,
              companyId: data.companyId
            },
            url: `/self-funding/repaymentSummaryWireRequest`,
            responseHandler: async response => await response.blob(),
            cache: 'no-cache'
          });

          const blobData = (await result.data) as Blob;
          if (!blobData) {
            throw new Error();
          }

          FileDownload(blobData, `Comprobante recarga.pdf`);

          return {
            data: null
          };
        } catch (error) {
          console.error('Error downloading file:', error);
          throw error;
        }
      }
    }),
    getSelfFundingMandateForm: build.query({
      query: (companyTaxIdentifier: string) => `/self-funding/getMandatePdf/${companyTaxIdentifier}`,
      providesTags: ['SelfFunding']
    })
  }),
  overrideExisting: false
});

export const {
  useGetCompaniesPaginateAllQuery,
  useGetCompaniesQuery,
  useEditCompaniesMutation,
  useGetSelfFundedAccountQuery,
  useGetSelfFundedAccountsQuery,
  useUpdateSelfFundingLowOnFundsThresholdMutation,
  useDownloadRepaymentSummaryWireFileMutation,
  useGetSelfFundingMandateFormQuery
} = extendedApi;
